import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, Sun, Waves, Languages } from "lucide-react";
import { useTheme, themeOrder } from "../lib/theme";

// Language Switcher Component
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'zh', name: '简体中文' },
    { code: 'ja', name: '日本語' },
    { code: 'ko', name: '한국어' },
    { code: 'vi', name: 'Tiếng Việt' },
    { code: 'pt', name: 'Português' },
    { code: 'th', name: 'ภาษาไทย' },
    { code: 'id', name: 'Bahasa' }
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full transition-colors"
        style={{ backgroundColor: 'var(--th-card-bg)', border: '2px solid var(--th-card-border)' }}
        data-testid="language-switcher"
      >
        <Languages className="w-4 h-4" style={{ color: 'var(--th-text-secondary)' }} />
        <span className="text-sm font-medium" style={{ color: 'var(--th-text-primary)' }}>
          {currentLang.code.toUpperCase()}
        </span>
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-full right-0 mt-2 rounded-xl shadow-xl overflow-hidden z-50"
          style={{ backgroundColor: 'var(--th-card-bg)', border: '2px solid var(--th-card-border)' }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left transition-colors ${
                i18n.language === lang.code ? 'font-bold' : ''
              }`}
              style={{ 
                backgroundColor: i18n.language === lang.code ? 'var(--th-background-alt)' : 'transparent',
                color: 'var(--th-text-primary)'
              }}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-sm">{lang.name}</span>
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// Theme Toggle for Header
const HeaderThemeToggle = () => {
  const { currentTheme, switchTheme } = useTheme();
  const { t } = useTranslation();

  const themeIcons = {
    sunshine: Sun,
    ocean: Waves
  };

  const nextTheme = () => {
    const currentIndex = themeOrder.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    switchTheme(themeOrder[nextIndex]);
  };

  const Icon = themeIcons[currentTheme];

  return (
    <button
      onClick={nextTheme}
      className="flex items-center gap-2 px-3 py-2 rounded-full transition-all"
      style={{ backgroundColor: 'var(--th-primary)', color: '#1E293B' }}
      data-testid="header-theme-toggle"
      title={t(`themes.${currentTheme}`)}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-semibold hidden sm:inline">{t(`themes.${currentTheme}`)}</span>
    </button>
  );
};

// Header Component
export const Header = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-md shadow-sm' : ''
      }`}
      style={{ backgroundColor: scrolled ? 'var(--th-header-bg)' : 'transparent' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2"
          >
            <Link to="/" className="flex items-center gap-2">
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                style={{ backgroundColor: 'var(--th-primary)' }}
              >
                <Sparkles className="w-7 h-7" style={{ color: '#1E293B' }} />
              </div>
              <span className="font-heading font-bold text-xl" style={{ color: 'var(--th-text-primary)' }}>
                Teacher Happy
              </span>
            </Link>
          </motion.div>
          
          <nav className="hidden md:flex items-center gap-4">
            <a href="/#method" className="font-medium transition-colors" style={{ color: 'var(--th-text-secondary)' }}>{t('nav.method')}</a>
            <a href="/#why-us" className="font-medium transition-colors" style={{ color: 'var(--th-text-secondary)' }}>{t('nav.whyUs')}</a>
            <a href="/#about" className="font-medium transition-colors" style={{ color: 'var(--th-text-secondary)' }}>{t('nav.about')}</a>
            <HeaderThemeToggle />
            <LanguageSwitcher />
            <a 
              href="https://portal.teacherhappy.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold px-4 py-2 rounded-full transition-all hover:scale-105"
              style={{ 
                backgroundColor: 'var(--th-card-bg)', 
                color: 'var(--th-text-primary)',
                border: '2px solid var(--th-card-border)'
              }}
              data-testid="header-signin"
            >
              {t('nav.signIn')}
            </a>
          </nav>

          {/* Mobile nav */}
          <div className="flex md:hidden items-center gap-2">
            <HeaderThemeToggle />
            <LanguageSwitcher />
            <a 
              href="https://portal.teacherhappy.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold px-3 py-2 rounded-full text-sm"
              style={{ 
                backgroundColor: 'var(--th-card-bg)', 
                color: 'var(--th-text-primary)',
                border: '2px solid var(--th-card-border)'
              }}
              data-testid="header-signin-mobile"
            >
              {t('nav.signIn')}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};
